import { Alert, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { batchAdd, batchProgress, batchUpdate } from '../../../api/ventura/location';
import { API_S3_LOCATION_UPLOAD_INFO_URL } from '../../../constants/apiUrls';
import { useConfig } from '../../../hooks/useConfig';
import { useConstants } from '../../../hooks/useConstants';
import Link from '../../../inmarkit/Link';
import { removeFileExtension } from '../../../util/helpers';
import { snakeCaseKeys } from '../../../util/object';
import { uploadFile } from '../../../util/services';
import FormEndOptions from '../../forms/FormEndOptions';
import S3Upload from '../../forms/S3Upload';

interface BatchLocationFormProps {
  adGroupId: number;
  adGroupName: string;
  lineItemId: number;
  momentsType: string;
  isUpdate?: boolean;
  onClose: () => void;
}

function BatchLocationForm({
  adGroupId,
  adGroupName,
  lineItemId,
  momentsType,
  isUpdate = false,
  onClose,
}: BatchLocationFormProps) {
  const { constants } = useConstants();
  const { apiHost } = useConfig();
  const [valid, setValid] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState(undefined);
  const VENTURA_UPLOADS_PREFIX_LOCATIONS = get(constants, 'LOCATION.VENTURA_UPLOADS_PREFIX_LOCATIONS', '');
  const form = useForm({
    defaultValues: {
      adGroupId,
      key: undefined,
    },
  });
  let isValid = false;

  const { handleSubmit, getValues } = form;

  const handleCancelBtnClick = () => {
    onClose();
  };

  function clean(field) {
    return field.trim().replace(/[^A-Za-z0-9\-. @|!~[\]_]/g, '');
  }

  const generateUploadKey = () => {
    const cleanName = clean(adGroupName);
    return `${VENTURA_UPLOADS_PREFIX_LOCATIONS}${lineItemId}/${adGroupId}_${new Date().getTime()}_${cleanName}`;
  };

  const onS3UploadFailure = ({ message }) => {
    setValid(false);
    setSeverity('error');
    setStatus(message);
  };

  const validateDuplicates = async (files) => {
    isValid = false;
    setValid(false);
    setUploadSuccess(false);

    const url =
      momentsType === 'NotMoments' ||
      momentsType === 'ListMoments' ||
      momentsType === 'inpathmoments' ||
      momentsType === 'inhomemoments'
        ? '/admin/locations/validate_duplicate_name'
        : '/admin/locations/validate_duplicate_poi_id';
    const file = files[0];
    const response = await uploadFile(apiHost, url, null, file);
    /*
    Here is a typical response
    {"valid":true}
    or
    { 
      "valid": false,
      "title": "Invalid POI Ids validation",
      "message": "The upload file contains the following invalid poi IDs: 100, 200"
    }
    */

    isValid = get(response, 'valid', false);
    setValid(isValid);
    if (!isValid) {
      const title = get(response, 'title', '');
      const message = get(response, 'message', '');
      setSeverity('error');
      setStatus(`${title}: ${message}`);
    }
  };

  const onS3UploadSuccess = () => {
    setUploadSuccess(true);
    // `valid` is used for React UI in render() and is not updated in time for this check.
    // `isValid` is not a React state and updated correctly.
    if (isValid) {
      setSeverity('success');
      setStatus('File upload successful. Select "Submit" to add locations to ad group.');
    }
  };

  const checkProgress = (response) => {
    const responseStatus = get(response, 'status', '');
    if (responseStatus.startsWith('Completed')) {
      setSeverity('success');
    } else {
      setSeverity('info');
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setTimeout(() => batchJobProgress.mutate(), 5000);
    }
    setStatus(responseStatus);
  };

  const batchProcess = useMutation({
    mutationFn: (payload) =>
      isUpdate ? batchUpdate(apiHost, adGroupId, payload) : batchAdd(apiHost, adGroupId, payload),
    onSuccess: (response) => {
      checkProgress(response);
      setValid(false);
    },
  });

  const batchJobProgress = useMutation({
    mutationFn: () => batchProgress(apiHost, removeFileExtension(getValues('key'))),
    onSuccess: (response) => {
      checkProgress(response);
    },
  });

  const onSubmit = (formData) => {
    const payload = snakeCaseKeys(formData);
    if (!payload.key) {
      setValid(false);
      setSeverity('error');
      setStatus('Sorry but the S3 key is missing. Please try attaching the file again.');
      return;
    }
    batchProcess.mutate(payload);
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <Stack spacing={1}>
        <Stack alignItems="flex-start" spacing={1}>
          <Typography variant="h5" fontWeight="bold">
            Batch {isUpdate ? 'Update' : 'Add'} Locations for {adGroupName}
          </Typography>
          <Paper>
            <Stack m={1} spacing={1}>
              <Typography variant="h5">Step 1: Upload File</Typography>
              {isUpdate && (
                <Typography>
                  Important: Only upload a file that was downloaded from this page. Please use download link below.
                  After you make your edits upload the file below. Note: you cannot change Id, campaign id, latitude,
                  longitude, or zipcode.
                </Typography>
              )}
              {!isUpdate && (
                <Typography>
                  <ul>
                    <li>
                      File format: Name, Address, Latitude, Longitude, Radius, Zipcode, Street, Location Type, State,
                      City, DMA, Congressional District, LV1, LV2, LV3, LV4, LV5, LV6, Targeting By, Bounded By,
                      Excluded, Preceptivity disabled
                    </li>
                    <li>All locations must have either lat+lon+radius and/or zipcode or Targeting</li>
                    <li>
                      If zipcode is provided along w/ lat+lon+radius, then the zipcode will only be used for reporting
                      purpose. The zipcode will not be used in the bidding operation of the campaign.
                    </li>
                    <li>
                      The value in Targeting By and Bounded By should be the corresponding shape type column name e.g.
                      state
                    </li>
                    <li>Shapes that can be targeted or used for bounding: state</li>
                    <li>Header is required.</li>
                  </ul>
                </Typography>
              )}
              <Stack spacing={1}>
                <S3Upload
                  name="key"
                  s3InfoUrl={API_S3_LOCATION_UPLOAD_INFO_URL}
                  generateKey={generateUploadKey}
                  accept=".xlsx,.csv"
                  onSuccess={onS3UploadSuccess}
                  onFailure={onS3UploadFailure}
                  onStart={validateDuplicates}
                />
                {isUpdate && (
                  <Stack direction="row" justifyContent="center">
                    <Link href={`${apiHost}/admin/campaigns/${adGroupId}/locations/index_csv`}>
                      <Button variant="outlined">Download Existing Locations</Button>
                    </Link>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Paper>
          <Paper sx={{ width: 1 }}>
            <Stack m={1} spacing={1}>
              <Typography variant="h5">Step 2: Batch Process File</Typography>
              {status && (
                <Stack direction="row" m={2}>
                  <Alert
                    sx={{ width: '100%' }}
                    severity={severity}
                    action={severity === 'info' ? <CircularProgress color="inherit" size={24} /> : undefined}
                  >
                    {status}
                  </Alert>
                </Stack>
              )}
              <Stack direction="row" justifyContent="center">
                <Button disabled={!(valid && uploadSuccess)} onClick={handleSubmit(onSubmit)}>
                  Submit
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        <FormEndOptions>
          <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
            <Button variant="outlined" onClick={handleCancelBtnClick}>
              Close
            </Button>
          </Stack>
        </FormEndOptions>
      </Stack>
    </FormProvider>
  );
}

export default BatchLocationForm;
